import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { ALL_EMAIL_TYPES, EMAIL_TYPE_LABELS, EMAIL_SAMPLE_VARS } from '@/lib/emailSampleVars';
import { Loader2, Mail, Send, CheckCircle2, XCircle, Inbox } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface SendResult {
  type: string;
  status: 'pending' | 'success' | 'error';
  message?: string;
}

export function EmailTester() {
  const { user } = useAuth();
  const adminEmail = user?.email || 'justus.wiedemann@celsios.io';

  const [emailType, setEmailType] = useState<string>('welcome');
  const [recipientEmail, setRecipientEmail] = useState(adminEmail);
  const [varsJson, setVarsJson] = useState<string>(JSON.stringify(EMAIL_SAMPLE_VARS.welcome ?? {}, null, 2));
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number; type: string } | null>(null);
  const [bulkResults, setBulkResults] = useState<SendResult[]>([]);
  const [bulkRunning, setBulkRunning] = useState(false);

  useEffect(() => {
    if (user?.email && recipientEmail === '') {
      setRecipientEmail(user.email);
    }
  }, [user?.email]);

  const handleEmailTypeChange = (value: string) => {
    setEmailType(value);
    setVarsJson(JSON.stringify(EMAIL_SAMPLE_VARS[value] ?? {}, null, 2));
    setPreviewHtml('');
  };

  const parsedVars = useMemo(() => {
    try {
      return JSON.parse(varsJson || '{}');
    } catch {
      return null;
    }
  }, [varsJson]);

  const handleSendTest = async () => {
    if (!recipientEmail) {
      toast.error('Please enter a recipient email');
      return;
    }
    if (parsedVars === null) {
      toast.error('Template variables are not valid JSON');
      return;
    }
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-transactional-email', {
        body: {
          to: recipientEmail,
          toName: 'Admin Test',
          emailType,
          templateVars: parsedVars,
          is_test: true,
        },
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data?.error || 'Send failed');
      toast.success(`Test email "${EMAIL_TYPE_LABELS[emailType] || emailType}" sent`);
    } catch (e: any) {
      toast.error(`Send failed: ${e?.message || 'unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  const handleSendAll = async () => {
    if (!recipientEmail) {
      toast.error('Please enter a recipient email');
      return;
    }
    const lastRun = sessionStorage.getItem('email_send_all_last');
    if (lastRun && Date.now() - parseInt(lastRun) < 60_000) {
      toast.error('Please wait at least 60 seconds between bulk runs.');
      return;
    }
    sessionStorage.setItem('email_send_all_last', String(Date.now()));

    setBulkRunning(true);
    setBulkResults(ALL_EMAIL_TYPES.map(t => ({ type: t, status: 'pending' as const })));

    for (let i = 0; i < ALL_EMAIL_TYPES.length; i++) {
      const t = ALL_EMAIL_TYPES[i];
      setBulkProgress({ current: i + 1, total: ALL_EMAIL_TYPES.length, type: t });
      try {
        const { data, error } = await supabase.functions.invoke('send-transactional-email', {
          body: {
            to: recipientEmail,
            toName: 'Admin Test (Bulk)',
            emailType: t,
            templateVars: EMAIL_SAMPLE_VARS[t] || {},
            is_test: true,
          },
        });
        if (error) throw error;
        if (data?.success === false) throw new Error(data?.error || 'Send failed');
        setBulkResults(prev => prev.map(r => r.type === t ? { ...r, status: 'success' } : r));
      } catch (e: any) {
        setBulkResults(prev => prev.map(r => r.type === t ? { ...r, status: 'error', message: e?.message } : r));
      }
      // throttle: 800ms between sends
      if (i < ALL_EMAIL_TYPES.length - 1) {
        await new Promise(r => setTimeout(r, 800));
      }
    }
    setBulkProgress(null);
    setBulkRunning(false);
    toast.success('Bulk run finished — check your inbox.');
  };

  const handlePreview = async () => {
    if (parsedVars === null) {
      toast.error('Template variables are not valid JSON');
      return;
    }
    setPreviewing(true);
    try {
      const { data, error } = await supabase.functions.invoke('preview-transactional-email', {
        body: { emailType, templateVars: parsedVars, testBanner: true },
      });
      if (error) throw error;
      if (!data?.html) throw new Error(data?.error || 'No HTML returned');
      setPreviewHtml(data.html);
    } catch (e: any) {
      toast.error(`Preview failed: ${e?.message || 'unknown error'}`);
    } finally {
      setPreviewing(false);
    }
  };

  // Auto-preview on type change (debounced) when JSON is valid
  useEffect(() => {
    if (parsedVars === null) return;
    const handle = setTimeout(() => {
      handlePreview();
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailType]);

  return (
    <div className="space-y-6">
      {/* Send all */}
      <Card className="app-tile">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="app-icon-box"><Inbox className="h-5 w-5 text-primary" /></div>
            <div>
              <CardTitle className="app-section-title">Send all email types</CardTitle>
              <CardDescription>
                Sends a [TEST] copy of every template to your admin inbox with realistic sample data.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bulkRecipient">Recipient (admin email)</Label>
            <Input
              id="bulkRecipient"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
            />
          </div>
          <Button onClick={handleSendAll} disabled={bulkRunning} className="w-full">
            {bulkRunning ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending {bulkProgress?.current}/{bulkProgress?.total} — {bulkProgress?.type}…</>
            ) : (
              <><Send className="mr-2 h-4 w-4" /> Send ALL {ALL_EMAIL_TYPES.length} email types to {recipientEmail}</>
            )}
          </Button>

          {bulkResults.length > 0 && (
            <div className="border rounded-lg divide-y max-h-72 overflow-auto">
              {bulkResults.map(r => (
                <div key={r.type} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="font-mono text-xs">{r.type}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-xs">{EMAIL_TYPE_LABELS[r.type] || ''}</span>
                    {r.status === 'pending' && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                    {r.status === 'success' && <CheckCircle2 className="h-4 w-4 text-success" />}
                    {r.status === 'error' && (
                      <span title={r.message} className="flex items-center gap-1">
                        <XCircle className="h-4 w-4 text-destructive" />
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Single send */}
      <Card className="app-tile">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="app-icon-box"><Mail className="h-5 w-5 text-primary" /></div>
            <div>
              <CardTitle className="app-section-title">Send a single test email</CardTitle>
              <CardDescription>Customise variables and target a single recipient.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email type</Label>
              <Select value={emailType} onValueChange={handleEmailTypeChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALL_EMAIL_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{EMAIL_TYPE_LABELS[t] || t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Recipient</Label>
              <Input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Template variables (JSON)</Label>
            <Textarea
              value={varsJson}
              onChange={(e) => setVarsJson(e.target.value)}
              rows={8}
              className="font-mono text-xs"
            />
            {parsedVars === null && (
              <p className="text-xs text-destructive">Invalid JSON — fix before sending.</p>
            )}
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSendTest} disabled={sending || parsedVars === null} className="flex-1">
              {sending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
              ) : (
                <><Send className="mr-2 h-4 w-4" /> Send test email</>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handlePreview}
              disabled={previewing || parsedVars === null}
            >
              {previewing ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rendering…</>
              ) : (
                'Preview email'
              )}
            </Button>
          </div>

          {previewHtml && (
            <div className="border rounded-lg overflow-hidden">
              <iframe
                title="Email preview"
                sandbox="allow-same-origin"
                srcDoc={previewHtml}
                className="w-full h-[680px] bg-background"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
